import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";

import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import ServerSummariesTable from "../tables/ServerSummariesTable";

import { useTranslation } from "react-i18next";

import "../report.scss";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import moment from "moment";
import Input from "components/form/input";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import Checkbox from "components/form/checkbox/Checkbox";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  email: string;
  queue_after_days: number;
  register_ids: number[];
  kitchen_grat: number;
  bar_grat: number;
  support_grat: number;
  include_zero_gratuity_orders: boolean;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function ServerSummaries() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const GetReportRun = useReportRun();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const { facilityStore } = useAppSelector(state => state);

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    email: "",
    queue_after_days: 14,
    register_ids: [],
    kitchen_grat: 0,
    bar_grat: 0,
    support_grat: 0,
    include_zero_gratuity_orders: false,
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport();
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];
    const sortedConfigurations = [...report.configurations].sort((prev, next) => prev.position - next.position);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      configurations: sortedConfigurations,
    }));
  }

  async function getReport() {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        register_ids: state.register_ids,
        kitchen_grat: state.kitchen_grat,
        bar_grat: state.bar_grat,
        support_grat: state.support_grat,
        include_zero_gratuity_orders: state.include_zero_gratuity_orders,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(reportRes.message));
      return;
    }

    if (reportRes.message) {
      dispatch(showError(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {
    const segmentsRes = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        date_range: state.date_range,
        kitchen_grat: state.kitchen_grat,
        bar_grat: state.bar_grat,
        support_grat: state.support_grat,
        email,
      },
      true,
    );

    if (segmentsRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.server_summaries.001")));
      return;
    }

    if (!email) {
      downloadCsv(segmentsRes.data as string, reportFilename() + '.csv');
    } else {
      dispatch(showSuccess(segmentsRes.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        date_range: state.date_range,
        kitchen_grat: state.kitchen_grat,
        bar_grat: state.bar_grat,
        support_grat: state.support_grat,
        email,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.server_summaries.002")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.server_summaries.003"),
    action: getReport,
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload('');
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint('');
    }
  }

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>, nonNumeric?: boolean) {
    const { value, checked, id } = e.target;
    let updateSelectedValues: any[] = [];
    if (state[id]) {
      updateSelectedValues = [...state[id]];
    }
    if (checked) {
      updateSelectedValues.push(nonNumeric ? value : Number(value));
    } else {
      const removeIndex = updateSelectedValues?.findIndex(valueId => valueId === (nonNumeric ? value : Number(value)));
      if (removeIndex !== -1) {
        updateSelectedValues.splice(removeIndex, 1);
      }
    }
    setState(prevState => ({ ...prevState, [id]: updateSelectedValues }));
  }

  function handleRemoveAll(id: string) {
    setState(prevState => ({ ...prevState, [id]: [] }));
  }

  function handleAddAllRegisters(id: string) {
    const updatedSelectedRegisters: any[] = [];
    state.allRegisters?.forEach((register: any) => {
      updatedSelectedRegisters.push(register?.id);
    });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedRegisters,
    }));
  }

  return (
    <Page
      title={t("secure.facility.reports.pages.server_summaries.004")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("secure.facility.reports.pages.server_summaries.005"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.reports.pages.server_summaries.006"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("secure.facility.reports.pages.server_summaries.007"),
              handler: handlePrint,
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("secure.facility.reports.pages.server_summaries.008"),
          url: "/admin/report",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
            <FilterDropdown
              label={t("Registers")}
              data={facilityStore.registers}
              dataLabelProperty={"title"}
              checkBoxId={"register_ids"}
              dataValueProperty="id"
              onCheckBoxChange={onCheckBoxChange}
              onClear={() => handleRemoveAll("register_ids")}
              onAddAll={() => handleAddAllRegisters("register_ids")}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
            <Input
              key="kitchen_grat"
              label={t('Kitchen Gratuity Percent')}
              id="kitchen_grat"
              onChange={e =>
                setState(prevState => ({ ...prevState, kitchen_grat: e.target.value }))
              }
              type="number"
              value={state.kitchen_grat || ""}
            />
            <Input
              key="bar_grat"
              label={t('Bar Gratuity Percent')}
              id="bar_grat"
              onChange={e =>
                setState(prevState => ({ ...prevState, bar_grat: e.target.value }))
              }
              type="number"
              value={state.bar_grat || ""}
            />
            <Input
              key="support_grat"
              label={t('Support Gratuity Percent')}
              id="support_grat"
              onChange={e =>
                setState(prevState => ({ ...prevState, support_grat: e.target.value }))
              }
              type="number"
              value={state.support_grat || ""}
            />
            <Checkbox
              id="include_zero_gratuity_orders"
              size="medium"
              value={state.includeClosedAccounts}
              checked={state.includeClosedAccounts}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setState(prev => ({
                  ...prev,
                  include_zero_gratuity_orders: e.target.checked,
                }))
              }
              label={t("Include $0 Gratuity Orders")}
            />

          </div>
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <ServerSummariesTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
